import React, { useMemo } from "react";
import { createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "../app";
import { useViewer } from "../hooks";
import { Bookmark, deleteBookmark, selectAllBookmarks } from "../redux/bookmarks-slice";
import LocalScene from "../viewer/js/projections";
import {isEqual} from 'lodash';
import { t } from "i18next";
import { selectAllAssets, getVisibleAssets, setVisibleAssets } from "../redux/assets-slice";
import { changeCameraState } from "../redux/camera-slice";
import { updateSettingsState } from "../redux/settings-slice";
import { ProviderProps } from "./props";

export const BookmarksContext = createContext({
  bookmarks: [] as Bookmark[],
  loadBookmark: (bookmarkID: string) : boolean => null,
  deleteBookmarks: () => {}
});

export const BookmarksProvider = (props: ProviderProps) => {
  const {viewer} = useViewer();
  const dispatch = useDispatch();

  const assets = useSelector(selectAllAssets);
  const bookmarks = useSelector(selectAllBookmarks);

  const visibleAssets = useMemo(() => {
    return getVisibleAssets(assets);
  }, [assets]);

  /**
   * Get visible bookmark assets, ignoring assets that have
   * been removed from the project
   **/
  const getValidAssetIDs = (bookmark: Bookmark) => {
    const assetIDs = assets.map(asset => asset.id);
    return bookmark.visibleAssets.filter(assetID => {
      return assetIDs.includes(assetID);
    });
  };

  const loadBookmark = (bookmarkID: string) => {
    if (!viewer) return;

    if (!LocalScene.initialized) {
      toast.warning(t('bookmarks.invalid_position'));
      return;
    }

    const bookmark = bookmarks.find(x => x.id === bookmarkID);
    if (!bookmark) return;

    dispatch(updateSettingsState(bookmark.settings));

    viewer.applySavedAerialState(bookmark.aerialState);

    const visibleAssetIDs = visibleAssets.map(asset => asset.id);
    const bookmarkAssetIDs = getValidAssetIDs(bookmark);

    visibleAssetIDs.sort();
    bookmarkAssetIDs.sort();

    if (isEqual(visibleAssetIDs, bookmarkAssetIDs)) {
      // Apply scene state directly
      viewer.applySavedSceneState(bookmark.sceneState, true);
    } else {
      // Set saved scene state and apply new asset visibility
      viewer.setInitialStateFlag(false);
      dispatch(changeCameraState(bookmark.sceneState));
      dispatch(setVisibleAssets(bookmarkAssetIDs));
    }

    return true;
  };

  const deleteBookmarks = () => {
    for (let bookmark of bookmarks) {
      dispatch(deleteBookmark(bookmark.id));
    }
  };

  return (
    <BookmarksContext.Provider value={{
      bookmarks,
      loadBookmark,
      deleteBookmarks
    }}>
      {props.children}
    </BookmarksContext.Provider>
  );
};